import {
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import type { MenuItemsType } from '@duke-hq/ui/components/SubSidebar/MenuItems';
import { SidebarLayout } from '@duke-hq/ui/components/SubSidebar/SidebarLayout';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { emptyPaginationResponse, SortOrder, parseSortOrder } from '@duke-hq/lib/pagination';
import { Archive, ArrowRightFromLine, Eye, UserPlus, Zap } from 'lucide-react';
import { Suspense, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { EnrolmentDetails } from './EnrolmentDetails';
import { EnrolmentsTable } from './EnrolmentsTable';
import { constructQueryParams } from '@duke-hq/lib';
import { StudentCourseEnrolmentStatus } from '@duke-hq/svc-api-db';
import { trpcAdmin, type RouterOutputs } from '../lib/trpcAdmin';
import type { CourseStatus } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import AdminFilterBar from '@duke-hq/app-admin-web/components/FilterBar/AdminFilterBar';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { withPermission } from '../withPermission';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { MAX_SEARCH_LENGTH } from '@duke-hq/shared-constants/searchCaps';

// includes all possible search, sort, filter and pagination params
export interface EnrolmentsListURLParams {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  page?: string | undefined;
  search?: string | undefined;
  status?: CourseStatus | undefined;
  name?: string | undefined;
  provider?: string | undefined;
  course?: string | undefined;
  contract?: string | undefined;
  health?: string | undefined;
  unitLag?: string | undefined;
  issue?: string | undefined;
  progress?: string | undefined;
  createdAt?: string | undefined;
  lastLogin?: string | undefined;
}

function generateMenuItems(
  filters: EnrolmentsListURLParams,
  enrolmentsReport: RouterOutputs['enrolment']['enrolmentsReport'] = {
    on_track: 0,
    overdue: 0,
    at_risk: 0,
    not_started: 0,
    less_than_25_percent: 0,
    between_25_and_75_percent: 0,
    between_75_and_100_percent: 0,
    completed_enrolments: 0,
    active_enrolments: 0,
    total_enrolments: 0,
    withdrawn_enrolments: 0,
    deleted_enrolments: 0,
    review_enrolments: 0,
  },
): MenuItemsType[] {
  const baseUrl = '/enrolments';
  const isNonEmptyEntry = (entry: [string, unknown]): entry is [string, string] =>
    typeof entry[1] === 'string' && entry[1] !== '';

  const { page, ...existingParams } = Object.fromEntries(
    Object.entries(filters).filter(isNonEmptyEntry),
  );
  return [
    {
      alias: 'Active',
      label: t`Active enrolments`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: 'IN_PROGRESS' })}`,
      icon: UserPlus,
      snapshot: enrolmentsReport.active_enrolments || 0,
    },
    {
      label: t`All`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: undefined })}`,
      icon: Zap,
      iconStyles: { transform: 'rotate(-90deg)' },
      snapshot: enrolmentsReport.total_enrolments || 0,
    },
    ...(enrolmentsReport.review_enrolments > 0
      ? [
          {
            alias: 'Needs review',
            label: t`Needs review`,
            link: `${baseUrl}${constructQueryParams({ ...existingParams, status: 'PENDING_REVIEW' })}`,
            icon: Eye,
            snapshot: enrolmentsReport.review_enrolments,
          },
        ]
      : []),
    {
      alias: 'Past',
      label: t`Past enrolments`,
      link: `${baseUrl}${constructQueryParams({
        ...existingParams,
        status: `${StudentCourseEnrolmentStatus.COMPLETED},${StudentCourseEnrolmentStatus.WITHDRAWN},${StudentCourseEnrolmentStatus.DELETED}`,
      })}`,
      icon: Archive,
      snapshot:
        (enrolmentsReport.completed_enrolments || 0) +
        (enrolmentsReport.withdrawn_enrolments || 0) +
        (enrolmentsReport.deleted_enrolments || 0),
    },
  ];
}

export const Enrolments = withPermission('canViewEnrolments', () => {
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { pageSize, currentPage, setPage } = usePagination();
  const navigate = useNavigate();
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const [searchParams] = useSearchParams();
  const { filters } = useFiltersContext();

  const querySearch = searchParams.get('search') ?? '';

  const allFilters = useMemo(() => {
    return {
      sort: parseSortOrder(searchParams.get('sort'), SortOrder.DESC),
      orderBy: searchParams.get('orderBy') ?? 'created_at',
      status: searchParams.get('status') ?? undefined,
      health: filters.health?.join(','),
      recentPaceStatus: filters.recentPaceStatus?.join(','),
      courseIds: filters.courses,
      provider: filters.provider,
      issue: filters.issue,
      consultantIds: filters.consultants,
      contractIds: filters.contractIds,
      siteLocationIds: filters.sites,
      siteRegionIds: filters.regions,
    };
  }, [filters, searchParams]);

  const { data: paginatedEnrolments, isFetching } = trpcAdmin.enrolment.list.useQuery(
    {
      search: querySearch.trim(),
      filters: allFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: enrolmentsReport } = trpcAdmin.enrolment.enrolmentsReport.useQuery({});
  const { data: providers } = trpcAdmin.providers.all.useQuery({});

  const sideDrawerItems = generateMenuItems(
    // Basically just til we can clean up EnrolmentsOld
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    Object.fromEntries(searchParams.entries()) as unknown as EnrolmentsListURLParams,
    enrolmentsReport,
  );
  const { isOpen } = useDisclosure({
    isOpen: Boolean(enrolmentId),
  });

  const onCloseHandler = useCallback(() => {
    navigate(`/enrolments?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  const getProviderName = useCallback(
    (providerId: number): string => {
      if (Array.isArray(providers) && providerId) {
        const provider = providers.find(({ id }) => id === providerId);
        if (provider) {
          return provider.name || '';
        }
      }
      return '';
    },
    [providers],
  );

  return (
    <>
      {enrolmentId !== undefined ? (
        <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <Suspense fallback={<CenteredSpinner />}>
              <EnrolmentDetails />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}

      <SidebarLayout items={sideDrawerItems} label={t`Enrolments`}>
        <TableViewLayout
          pageHeader={[
            !isExpanded && (
              <IconButton
                aria-label="expand secondary sidebar"
                key={'toggle-expand-sub-sidebar'}
                variant="tertiary"
                onClick={toggleExpandSubSidebar}
                size="md"
                icon={<ArrowRightFromLine />}
              />
            ),
            <PageHeading key="page-heading">
              {searchParams.get('status') === 'IN_PROGRESS' ? (
                <Trans>Active Enrolments</Trans>
              ) : searchParams.get('status') === 'PENDING_REVIEW' ? (
                <Trans>Enrolments Needing Review</Trans>
              ) : searchParams.get('status')?.includes('COMPLETED') === true ? (
                <Trans>Past Enrolments</Trans>
              ) : (
                <Trans>All Enrolments</Trans>
              )}
            </PageHeading>,
          ]}
          filters={
            <>
              <SearchInput maxW="search-input" maxLength={MAX_SEARCH_LENGTH} />
              <AdminFilterBar />
            </>
          }
          tableActionsLeft={
            <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
              <Plural
                value={paginatedEnrolments.pagination.totalCount || 0}
                zero="0 enrolments"
                one="1 enrolment"
                other="# enrolments"
              />
            </Text>
          }
          table={
            <>
              <EnrolmentsTable
                enrolments={paginatedEnrolments.items}
                getProviderName={getProviderName}
              />
              {isFetching ? (
                <Center p={8}>
                  <Spinner size="xl" />
                </Center>
              ) : paginatedEnrolments.items.length === 0 ? (
                <Center p={8}>
                  <Text textAlign="center">
                    <Trans>No enrolments found</Trans>
                  </Text>
                </Center>
              ) : null}
            </>
          }
          pagination={
            paginatedEnrolments.items.length > 0 ? (
              <Pagination pagination={paginatedEnrolments.pagination} onPageChange={setPage} />
            ) : null
          }
        />
      </SidebarLayout>
    </>
  );
});
